import { useState } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format as formatDate, isValid as isValidDate, parse as parseDate } from 'date-fns';
import type { SxProps, Theme } from '@mui/material';

const ISO_DATE = 'yyyy-MM-dd';

const cursorStyles: SxProps<Theme> = { cursor: 'pointer' };

const parseIsoDate = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const d = parseDate(value, ISO_DATE, new Date());
  return isValidDate(d) ? d : null;
};

const formatIsoDate = (date: Date | null): string | null => {
  if (!date || !isValidDate(date)) {
    return null;
  }
  return formatDate(date, ISO_DATE);
};

type FormDatePickerProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label: string;
  /** Stored as ISO date string (yyyy-MM-dd) in the form values. */
};

/**
 * DatePicker bound to react-hook-form. Stores values as ISO date strings
 * (yyyy-MM-dd) and opens the calendar when the user clicks anywhere on the
 * text field (not just the icon button) — mirrors the WhenSection pattern.
 */
export function FormDatePicker<TFieldValues extends FieldValues>({
  control,
  name,
  label,
}: FormDatePickerProps<TFieldValues>) {
  const [open, setOpen] = useState(false);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <DatePicker
          label={label}
          value={parseIsoDate(field.value as string | null | undefined)}
          onChange={(date) => field.onChange(formatIsoDate(date))}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!fieldState.error,
              helperText: fieldState.error?.message,
              onClick: () => setOpen(true),
              sx: cursorStyles,
            },
            field: { clearable: true },
          }}
        />
      )}
    />
  );
}
